import { collectFingerprintFeatures } from "./core/orchestrator";
import {
  postFingerprint,
  readContinuityHintFromStorage,
  persistContinuityToken,
} from "./core/transport";
import { encryptPayload } from "./crypto";
import { SDK_VERSION } from "./version";
import type {
  FingerprintConfig,
  FingerprintResult,
  FingerprintSDK,
  BrowserFeatures,
} from "./types";
import { FingerprintError, FingerprintTimeoutError, FingerprintNetworkError } from "./types";

const DEFAULT_CONFIG: Required<Omit<FingerprintConfig, "onError">> & {
  onError?: FingerprintConfig["onError"];
} = {
  apiEndpoint: "https://fp.techstudio.live",
  publicApiKey: "",
  headers: {},
  timeout: 10000,
  debug: false,
  allowPlaintext: false,
  onError: undefined,
};

/**
 * Empty / degraded fingerprint result returned when collection or transport
 * fails. The SDK never throws from get() — consumers detect failure by
 * checking `result.visitorId === ""` and/or registering `onError`.
 */
function emptyResult(): FingerprintResult {
  return {
    visitorId: "",
    deviceCoreId: "",
    browserScopedId: "",
    observationId: "",
    ip: null,
    flags: {
      isVpn: "unlikely",
      isIncognito: "unlikely",
      isBot: "unlikely",
    },
    scores: { vpn: 0, incognito: 0, bot: 0 },
    timestamp: new Date().toISOString(),
  };
}

class HeliosFingerprintSDK implements FingerprintSDK {
  private config: typeof DEFAULT_CONFIG;

  constructor(config: FingerprintConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    if (
      !this.config.publicApiKey &&
      this.config.apiEndpoint.includes("api.fingerprint.techstudio.live")
    ) {
      console.warn(
        "[Helios Fingerprint] No publicApiKey provided. Some features may not work correctly.",
      );
    }
  }

  /**
   * Get fingerprint result. Never throws — failures resolve to a degraded
   * result (visitorId === "", flags unlikely, scores 0) and invoke the
   * configured `onError` callback when set.
   */
  async get(options: { timeout?: number; debug?: boolean } = {}): Promise<FingerprintResult> {
    const requestConfig = {
      timeout: options.timeout ?? this.config.timeout,
      debug: options.debug ?? this.config.debug,
    };

    try {
      const features = await this.collectFeatures();
      return await this.sendToAPI(features, requestConfig);
    } catch (error: unknown) {
      const fpError =
        error instanceof FingerprintError
          ? error
          : new FingerprintError(
              (error as { message?: string })?.message || "Unknown error occurred",
              "UNKNOWN_ERROR",
            );
      this.invokeOnError(fpError);
      return emptyResult();
    }
  }

  private invokeOnError(err: FingerprintError): void {
    if (!this.config.onError) return;
    try {
      this.config.onError(err);
    } catch {
      /* swallow callback errors so they can't break the never-throw guarantee */
    }
  }

  private async collectFeatures(): Promise<BrowserFeatures> {
    try {
      return await collectFingerprintFeatures();
    } catch (error: unknown) {
      throw new FingerprintError(
        `Failed to collect fingerprint features: ${(error as { message?: string })?.message || error}`,
        "COLLECTION_ERROR",
      );
    }
  }

  private async sendToAPI(
    features: BrowserFeatures,
    options: { timeout: number; debug: boolean },
  ): Promise<FingerprintResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout);

    try {
      const headers: Record<string, string> = { ...this.config.headers };
      if (this.config.publicApiKey) {
        headers["Authorization"] = `Bearer ${this.config.publicApiKey}`;
        headers["X-API-Key"] = this.config.publicApiKey;
      }

      const endpoint = `${this.config.apiEndpoint}/v1/collect`;

      // Continuity-hint replay (Phase 13.7): include the prior server-issued
      // HMAC-signed visitor hint, if any, so server can promote medium-tier
      // matches to strong. Never feeds into device_core_id — soft signal only.
      const continuityHint = readContinuityHintFromStorage();
      const payload: Record<string, unknown> = {
        features,
        debug: options.debug,
        // Phase 13.16: identify this build so server can record into
        // observations.sdk_version for deprecation / debug analytics.
        sdk_version: SDK_VERSION,
      };
      if (continuityHint) payload.continuity_hint = continuityHint;

      const encryptedBody = await encryptPayload(JSON.stringify(payload));
      if (encryptedBody.v === 0 && !this.config.allowPlaintext) {
        throw new FingerprintError(
          "crypto.subtle unavailable — refusing to send plaintext fingerprint payload. " +
            "Use HTTPS, or pass `allowPlaintext: true` with a test environment API key.",
          "CRYPTO_UNAVAILABLE",
        );
      }

      const response = await postFingerprint({
        endpoint,
        body: JSON.stringify(encryptedBody),
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new FingerprintNetworkError(
          parseApiError(response.text, response.status),
          response.status,
        );
      }

      let responseData: any;
      try {
        responseData = JSON.parse(response.text);
      } catch {
        throw new FingerprintError("Invalid JSON response from API", "INVALID_RESPONSE");
      }

      // Persist the server-issued continuity token so the next get() can replay
      // it. Shape: { visitor_id, issued_at, sig } — matches ContinuityToken
      // interface. Server only emits this when CONTINUITY_HMAC_SECRET is set.
      if (
        responseData?.continuity?.visitor_id &&
        typeof responseData.continuity.issued_at === "number" &&
        responseData.continuity.sig
      ) {
        persistContinuityToken(responseData.continuity);
      }

      return this.transformAPIResponse(responseData, features, options.debug);
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error?.name === "AbortError") {
        throw new FingerprintTimeoutError(options.timeout);
      }
      if (error instanceof FingerprintError) {
        throw error;
      }
      throw new FingerprintNetworkError(error?.message || "Network request failed", undefined);
    }
  }

  /**
   * Transform API response to standardized SDK format. Server emits
   * snake_case only (see server/src/routes/collect.ts).
   */
  private transformAPIResponse(
    apiResponse: any,
    features: BrowserFeatures,
    includeDebug: boolean,
  ): FingerprintResult {
    void features;
    const result: FingerprintResult = {
      visitorId: apiResponse.visitor_id || "",
      deviceCoreId: apiResponse.device_core_id || "",
      browserScopedId: apiResponse.browser_scoped_id || "",
      observationId: apiResponse.observation_id || "",
      ip: apiResponse.ip || null,
      flags: {
        isVpn: apiResponse.flags?.is_vpn || "unlikely",
        isIncognito: apiResponse.flags?.is_incognito || "unlikely",
        isBot: apiResponse.flags?.is_bot || "unlikely",
      },
      scores: {
        vpn: apiResponse.scores?.vpn || 0,
        incognito: apiResponse.scores?.incognito || 0,
        bot: typeof apiResponse.scores?.bot === "number" ? apiResponse.scores.bot : 0,
      },
      timestamp: apiResponse.timestamp || new Date().toISOString(),
    };

    if (apiResponse.algo_version != null) {
      result.algoVersion = String(apiResponse.algo_version);
    }
    if (apiResponse.visitor_link != null) {
      result.visitorLink = apiResponse.visitor_link;
    }

    if (includeDebug && apiResponse.fingerprint) {
      result.fingerprint = apiResponse.fingerprint;
      result.verification = apiResponse.verification;
      result.visitor_linking = apiResponse.visitor_linking;
    }

    return result;
  }
}

/** Extract a human message from an API error response body. */
function parseApiError(responseText: string, status: number): string {
  try {
    const errorData = JSON.parse(responseText);
    if (errorData.error && typeof errorData.error === "object") {
      const errorObj = errorData.error;
      const validationErrors: string[] = [];
      Object.keys(errorObj).forEach((key) => {
        if (key !== "_errors" && errorObj[key]?._errors) {
          validationErrors.push(`${key}: ${errorObj[key]._errors.join(", ")}`);
        }
      });
      return validationErrors.length > 0
        ? `Validation error: ${validationErrors.join("; ")}`
        : JSON.stringify(errorData.error);
    }
    return errorData.message || errorData.error || `API error (${status})`;
  } catch {
    return responseText || `API error (${status})`;
  }
}

/**
 * Load function to create SDK instance. Throws only on non-browser environments
 * (a sanity check, not a user-facing error path) — fp.get() itself never throws.
 */
export async function load(config: FingerprintConfig = {}): Promise<FingerprintSDK> {
  if (typeof window === "undefined") {
    throw new FingerprintError(
      "Helios Fingerprint SDK can only be used in browser environments",
      "INVALID_ENVIRONMENT",
    );
  }
  return new HeliosFingerprintSDK(config);
}
